/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import axios from 'axios';
import { getRequiredEnvVar } from '@shared/utils/validate-env';
import { buildPortalIntegrationHeaders } from '@shared/utils/build-portal-integration-headers';
import { withAntiBotRetry } from '@shared/utils/with-antibot-retry';
import { withoutLegacyLeadAdUrl } from '@shared/utils/ad-dialog-helpers';

/** Base URL for the portal-integration API endpoints. */
export const API_BASE = getRequiredEnvVar('REACT_APP_API_URL');

/** The Facebook-settings payload shape returned/sent by the portal-integration API. */
export interface FacebookSettings {
  agency_id: string;
  fb_connected: boolean;
  fb_page_id?: string | null;
  fb_page_name?: string | null;
  fb_ad_account_id?: string | null;
  fb_token_expires_at?: string | null;
  fb_lead_ad_budget: number;
  fb_lead_ad_duration: number;
  /** Instagram's own boost/Lead Ad daily budget (cents) and duration (days) --
   * kept separate from fb_lead_ad_* so each platform's ad spend is
   * independently configurable, even though both bill through the same
   * Facebook ad account (fb_ad_account_id/fb_has_payment_method). */
  ig_lead_ad_budget: number;
  ig_lead_ad_duration: number;
  /** Lead Ad content/targeting options shared by Facebook and Instagram (an empty string means "use the built-in default", except the form question where empty means "no extra question"). */
  lead_ad_headline: string;
  lead_ad_message_closing: string;
  lead_ad_radius_km: number;
  lead_ad_form_question: string;
  lead_ad_thank_you_message: string;
  lead_ad_website_url: string;
  lead_ad_privacy_url: string;
  fb_marketplace_enabled: boolean;
  fb_has_payment_method?: boolean;
  /** Instagram linked to the connected Facebook Page. Cleared alongside fb_* fields on Facebook disconnect -- it cannot exist without the Page. */
  ig_account_id?: string | null;
  ig_account_name?: string | null;
  enabled_portals: string[];
  /** Native Instagram Business Login connection (no Facebook Page required). */
  ig_native_connected: boolean;
  ig_native_account_id?: string;
  ig_native_username?: string;
  ig_native_token_expires_at?: string;
  /** Agency's chosen, ordered field selection for the default post description. Null/absent means "use the built-in default order". */
  post_description_fields?: string[] | null;
}

/**
 * Housien supplies no Lead Ad content or amounts of its own: what leads see and
 * what is spent is the agency's responsibility, so everything starts empty. The
 * links, the budget and the duration are required before a Lead Ad (or, for the
 * budget and duration, a boost) can be created. The radius is the one exception:
 * Meta requires a city radius for Housing ads, so an unset one is the smallest
 * Meta accepts (the backend uses the same).
 */
export const LEAD_AD_CONTENT_DEFAULTS = {
  lead_ad_headline: '',
  lead_ad_message_closing: '',
  lead_ad_radius_km: 25,
  lead_ad_form_question: '',
  lead_ad_thank_you_message: '',
  lead_ad_website_url: '',
  lead_ad_privacy_url: '',
};

/** Empty record for a freshly-connected (or never-configured) Facebook settings: a budget or duration of 0 means "not set yet". */
export const DEFAULTS: Omit<FacebookSettings, 'agency_id'> = {
  fb_connected: false,
  fb_lead_ad_budget: 0,
  fb_lead_ad_duration: 0,
  ig_lead_ad_budget: 0,
  ig_lead_ad_duration: 0,
  ...LEAD_AD_CONTENT_DEFAULTS,
  fb_marketplace_enabled: true,
  enabled_portals: [],
  ig_native_connected: false,
};

/**
 * Performs an authenticated, anti-bot-protected Facebook settings API request.
 *
 * @param path - The API path (appended to the base URL).
 * @param method - The HTTP method.
 * @param body - Optional request body.
 * @returns The parsed response data.
 */
export async function apiRequest(path: string, method: string, body?: unknown) {
  const res = await withAntiBotRetry(
    (headers) =>
      axios({
        url: `${API_BASE}${path}`,
        method: method.toLowerCase() as 'get' | 'post' | 'put' | 'patch' | 'delete',
        headers,
        data: body,
      }),
    (opts) => buildPortalIntegrationHeaders('portal_facebook_settings', opts),
  );
  return res.data;
}

/**
 * Normalizes a raw Facebook settings API payload into a well-typed object.
 *
 * @param raw - The raw API payload.
 * @param agencyId - Fallback agency id when absent from the payload.
 * @returns The normalized Facebook settings.
 */
export function shapeSettings(raw: any, agencyId: string): FacebookSettings {
  return {
    agency_id: raw?.agency_id ?? agencyId,
    fb_connected: Boolean(raw?.fb_connected),
    fb_page_id: raw?.fb_page_id,
    fb_page_name: raw?.fb_page_name,
    fb_ad_account_id: raw?.fb_ad_account_id,
    fb_token_expires_at: raw?.fb_token_expires_at,
    fb_lead_ad_budget: raw?.fb_lead_ad_budget ?? DEFAULTS.fb_lead_ad_budget,
    fb_lead_ad_duration: raw?.fb_lead_ad_duration ?? DEFAULTS.fb_lead_ad_duration,
    ig_lead_ad_budget: raw?.ig_lead_ad_budget ?? DEFAULTS.ig_lead_ad_budget,
    ig_lead_ad_duration: raw?.ig_lead_ad_duration ?? DEFAULTS.ig_lead_ad_duration,
    lead_ad_headline: raw?.lead_ad_headline ?? DEFAULTS.lead_ad_headline,
    lead_ad_message_closing: raw?.lead_ad_message_closing ?? DEFAULTS.lead_ad_message_closing,
    lead_ad_radius_km: raw?.lead_ad_radius_km ?? DEFAULTS.lead_ad_radius_km,
    lead_ad_form_question: raw?.lead_ad_form_question ?? DEFAULTS.lead_ad_form_question,
    lead_ad_thank_you_message: raw?.lead_ad_thank_you_message ?? DEFAULTS.lead_ad_thank_you_message,
    lead_ad_website_url: withoutLegacyLeadAdUrl(raw?.lead_ad_website_url),
    lead_ad_privacy_url: withoutLegacyLeadAdUrl(raw?.lead_ad_privacy_url),
    fb_marketplace_enabled: raw?.fb_marketplace_enabled ?? DEFAULTS.fb_marketplace_enabled,
    fb_has_payment_method: Boolean(raw?.fb_has_payment_method),
    ig_account_id: raw?.ig_account_id ?? undefined,
    ig_account_name: raw?.ig_account_name ?? undefined,
    enabled_portals: Array.isArray(raw?.enabled_portals) ? raw.enabled_portals : [],
    ig_native_connected: Boolean(raw?.ig_native_connected),
    ig_native_account_id: raw?.ig_native_account_id ?? undefined,
    ig_native_username: raw?.ig_native_username ?? undefined,
    ig_native_token_expires_at: raw?.ig_native_token_expires_at ?? undefined,
    post_description_fields: Array.isArray(raw?.post_description_fields)
      ? raw.post_description_fields
      : undefined,
  };
}

/**
 * Starts the Instagram Business Login OAuth flow for an agency.
 *
 * Mirrors the OLX connect pattern (an authenticated call that returns the
 * third-party authorization URL, rather than Facebook's bare redirect to a
 * public backend route) since it also gives us a real disconnect endpoint.
 *
 * @param agencyId - The agency id.
 * @returns The Instagram authorization URL to redirect the browser to.
 */
export async function fetchInstagramAuthorizationUrl(agencyId: string): Promise<string> {
  const data = await apiRequest(
    `/portal-integration/agencies/${agencyId}/settings/instagram-native/connect`,
    'POST',
  );
  return data?.authorization_url ?? '';
}

/**
 * Disconnects the native Instagram Business Login connection for an agency.
 *
 * @param agencyId - The agency id.
 */
export async function disconnectInstagramNative(agencyId: string): Promise<void> {
  await apiRequest(
    `/portal-integration/agencies/${agencyId}/settings/instagram-native/disconnect`,
    'POST',
  );
}

/**
 * Returns the number of whole days remaining until a token expiry timestamp.
 *
 * @param expiresAt - ISO-8601 expiry timestamp, or undefined.
 * @returns Days remaining (negative if already expired), or null when absent/invalid.
 */
export function daysUntilExpiry(expiresAt?: string): number | null {
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt).getTime();
  if (Number.isNaN(expiry)) return null;
  return Math.floor((expiry - Date.now()) / (1000 * 60 * 60 * 24));
}
